import React, { useState, useEffect, useCallback, useRef } from "react";
import { LoadingSpinner } from "../LoadingSpinner";
import { LoadingSpinnerMeta } from "../../data";
import { AreaAvailability } from "../../data/types";
import { API_BASE_URL } from "../../data";
import { LocationIcon, UsersIcon, CheckCircleIcon, ArrowsExpandIcon, ArrowsContractIcon } from "../../icons";

type Props = {
    meta: { title: string, description: string }
    eventId: number
    isActive: boolean
    areaIds: number[]
}

const AREAS_PER_PAGE = 6;
const ROTATION_INTERVAL_MS = 5000;

export const ShiftOperations = (props: Props) => {
    const { meta, eventId, isActive } = props;

    const [areas, setAreas] = useState<AreaAvailability[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Carousel State
    const [currentIndex, setCurrentIndex] = useState(0);
    const [progress, setProgress] = useState(0);

    // Full Screen State
    const [isFullScreen, setIsFullScreen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const fetchShiftsAvailability = useCallback(async () => {
        if (!eventId) return;
        if (areas.length === 0) setLoading(true);

        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/check-in/event/${eventId}/urgent-shifts`, {
                credentials: 'include'
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to load areas');
            }
            const data: AreaAvailability[] = await response.json();
            setAreas(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    }, [eventId, areas.length]);

    useEffect(() => {
        if (isActive && eventId) {
            fetchShiftsAvailability();
            const refreshInterval = setInterval(fetchShiftsAvailability, 30000);
            return () => clearInterval(refreshInterval);
        }
    }, [isActive, eventId, fetchShiftsAvailability]);

    // Handle Full Screen Toggle
    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handleFullScreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullScreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
    }, []);

    const getSortedAreas = (): AreaAvailability[] =>
        [...areas].sort((a, b) => b.urgency_score - a.urgency_score);

    const sortedAreas = getSortedAreas();
    const totalPages = Math.ceil(sortedAreas.length / AREAS_PER_PAGE);

    // Carousel Logic
    useEffect(() => {
        if (sortedAreas.length <= AREAS_PER_PAGE) return;

        setProgress(0);

        const progressInterval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) return 0;
                return prev + (100 / (ROTATION_INTERVAL_MS / 100));
            });
        }, 100);

        const rotationInterval = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % totalPages);
            setProgress(0);
        }, ROTATION_INTERVAL_MS);

        return () => {
            clearInterval(progressInterval);
            clearInterval(rotationInterval);
        };
    }, [totalPages, sortedAreas.length]);

    const visibleAreas = sortedAreas.slice(
        currentIndex * AREAS_PER_PAGE,
        (currentIndex + 1) * AREAS_PER_PAGE
    );

    return (
        <div
            ref={containerRef}
            className={`flex-1 flex flex-col h-full bg-white dark:bg-gray-900 transition-all duration-300 ${isFullScreen ? 'p-8 overflow-hidden' : 'pb-8 pt-4'}`}
        >
            <div className={`container mx-auto px-4 ${isFullScreen ? 'max-w-full h-full' : 'max-w-5xl'} flex-1 flex flex-col`}>

                {/* Header */}
                <div className="flex justify-between items-start mb-8 relative">
                    <div className="flex-1 text-center">
                        <h2 className={`${isFullScreen ? 'text-5xl mb-4' : 'text-3xl mb-2'} font-bold text-gray-900 dark:text-gray-100 tracking-tight transition-all`}>
                            {meta.title}
                        </h2>
                        {!isFullScreen && (
                            <p className="text-gray-500 dark:text-gray-400 text-lg mt-1 max-w-2xl mx-auto">
                                {meta.description}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={toggleFullScreen}
                        className="absolute end-0 top-0 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
                    >
                        {isFullScreen ? <ArrowsContractIcon className="w-6 h-6" /> : <ArrowsExpandIcon className="w-6 h-6" />}
                    </button>
                </div>

                {loading && (
                    <div className="flex justify-center items-center py-24 flex-1">
                        <LoadingSpinner meta={LoadingSpinnerMeta} />
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm text-center mb-6">
                        {error}
                    </div>
                )}

                {!loading && !error && sortedAreas.length > 0 ? (
                    <div className="flex-1 flex flex-col justify-between h-full">

                        {/* Area Cards Grid */}
                        <div className={`grid ${isFullScreen ? 'grid-cols-3 gap-8 px-8' : 'grid-cols-2 md:grid-cols-3 gap-4'} auto-rows-fr transition-all`}>
                            {visibleAreas.map((area) => {
                                const allocated  = area.area_allocated  ?? 0;
                                const checkedIn  = area.area_checked_in ?? 0;
                                const spotsRemaining = Math.max(0, allocated - checkedIn);
                                const isFull = allocated > 0 && checkedIn >= allocated;
                                const progressPercent = allocated > 0 ? Math.min(100, (checkedIn / allocated) * 100) : 0;

                                let statusColor = 'border-s-primary-500';
                                if (isFull) statusColor = 'border-s-green-500';
                                else if (progressPercent < 30 && allocated > 0) statusColor = 'border-s-red-500';

                                return (
                                    <div
                                        key={area.id}
                                        className={`
                                            group relative flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700
                                            ${statusColor} border-s-[6px] overflow-hidden transform transition-all duration-500
                                            ${isFullScreen ? 'scale-100 shadow-lg' : ''}
                                        `}
                                    >
                                        <div className={`${isFullScreen ? 'p-6' : 'p-4'} flex flex-col h-full justify-between`}>

                                            {/* Status Badge */}
                                            <div className="flex justify-between items-start mb-3">
                                                <div className={`flex items-center text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-100 dark:bg-gray-700 rounded font-semibold ${isFullScreen ? 'text-sm px-3 py-1.5' : 'text-xs px-2 py-1'}`}>
                                                    <LocationIcon className={`${isFullScreen ? 'w-4 h-4 me-2' : 'w-3 h-3 me-1'} opacity-70`} />
                                                    Area
                                                </div>

                                                {isFull ? (
                                                    <span className={`inline-flex items-center rounded-md font-bold bg-green-50 text-green-700 border border-green-200 ${isFullScreen ? 'px-3 py-1.5 text-sm' : 'px-2 py-0.5 text-xs'}`}>
                                                        <CheckCircleIcon className={`${isFullScreen ? 'w-4 h-4 me-1.5' : 'w-3 h-3 me-1'}`} /> FULL
                                                    </span>
                                                ) : (
                                                    <span className={`inline-flex items-center rounded-md font-bold bg-primary-600 text-white shadow-sm ${isFullScreen ? 'px-4 py-1.5 text-lg' : 'px-2.5 py-0.5 text-sm'}`}>
                                                        {spotsRemaining} OPEN
                                                    </span>
                                                )}
                                            </div>

                                            {/* Area Name */}
                                            <h3 className={`font-extrabold leading-tight text-gray-900 dark:text-white ${isFullScreen ? 'text-3xl mb-4' : 'text-lg mb-3'}`}>
                                                {area.name}
                                            </h3>

                                            {/* Check-in Count */}
                                            <div className={`flex items-center font-bold text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-700/50 rounded-lg mb-3 ${isFullScreen ? 'px-4 py-2 text-2xl' : 'px-3 py-1.5 text-base'}`}>
                                                <UsersIcon className={`${isFullScreen ? 'w-6 h-6 me-3' : 'w-4 h-4 me-2'} text-gray-500`} />
                                                <span>{checkedIn} / {allocated} checked in</span>
                                            </div>

                                            {/* Progress Bar */}
                                            <div className={`w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden ${isFullScreen ? 'h-4' : 'h-2.5'}`}>
                                                <div
                                                    className={`h-full rounded-full transition-all duration-500 ${
                                                        isFull ? 'bg-green-500' :
                                                        progressPercent > 80 ? 'bg-amber-500' :
                                                        progressPercent < 30 && allocated > 0 ? 'bg-red-500' :
                                                        'bg-blue-600'
                                                    }`}
                                                    style={{ width: `${progressPercent}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Page Indicator / Timer */}
                        {totalPages > 1 && (
                            <div className={`flex flex-col items-center gap-4 ${isFullScreen ? 'mt-12 mb-8' : 'mt-8'}`}>
                                <div className="flex gap-2">
                                    {Array.from({ length: totalPages }).map((_, idx) => (
                                        <div
                                            key={idx}
                                            className={`rounded-full transition-all duration-300 ${idx === currentIndex ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-700'}
                                            ${isFullScreen ? (idx === currentIndex ? 'w-12 h-4' : 'w-4 h-4') : (idx === currentIndex ? 'w-8 h-2' : 'w-2 h-2')}`}
                                        />
                                    ))}
                                </div>
                                <div className={`bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden ${isFullScreen ? 'w-1/2 h-2' : 'w-64 h-1'}`}>
                                    <div
                                        className="h-full bg-primary-500 transition-all duration-100 ease-linear"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                                <p className={`text-gray-400 uppercase tracking-widest font-semibold ${isFullScreen ? 'text-lg' : 'text-xs'}`}>
                                    Page {currentIndex + 1} of {totalPages}
                                </p>
                            </div>
                        )}
                    </div>
                ) : !loading && !error && (
                    <div className="flex flex-col items-center justify-center py-24 px-4 flex-1">
                        <CheckCircleIcon className="w-32 h-32 text-gray-300 mb-8" />
                        <h3 className="text-5xl font-bold text-gray-900 dark:text-white">All Clear</h3>
                        <p className="text-gray-500 dark:text-gray-400 text-2xl mt-4">
                            No areas require attention at this moment.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
